import {
  mergeTagsResultSchema,
  tagSchema,
  taxonomyListResultSchema,
  type MergeTagsRequest,
  type MergeTagsResult,
  type Tag,
  type TaxonomyListResult,
} from '@xphoto/shared';
import { ApiError } from '../gallery/api';
import { apiFetch } from '../lib/http';

/**
 * Client typé de la GESTION DE LA TAXONOMIE (T5 lot B, XPHOTO-26). Consomme le backend
 * taxonomie (liste + fusion) et réutilise les routes tags existantes (créer / renommer /
 * supprimer). Chaque réponse est RE-PARSÉE via son schéma partagé `@xphoto/shared`
 * (« never trust the client », y compris ses propres réponses).
 *
 * Les erreurs sont propagées en `ApiError` (statut + code) — indispensable pour
 * DISCRIMINER le 409 `CONFLICT` (étiquette déjà prise) lors d'une création/renommage.
 */

async function readJson(res: Response): Promise<unknown> {
  if (!res.ok) {
    let code = 'UNKNOWN';
    try {
      const body = (await res.json()) as { error?: { code?: string } };
      if (typeof body?.error?.code === 'string') code = body.error.code;
    } catch {
      // Corps non-JSON : on garde le code générique.
    }
    throw new ApiError(res.status, code);
  }
  return res.json();
}

/** `GET /api/libraries/:id/taxonomy` — tout le vocabulaire + usages (zéro-usage inclus). */
export async function fetchTaxonomy(
  libraryId: string,
  signal?: AbortSignal,
): Promise<TaxonomyListResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/taxonomy`, { signal: signal ?? null });
  return taxonomyListResultSchema.parse(await readJson(res));
}

/** `POST /api/libraries/:id/tags` — crée un tag (409 `CONFLICT` si l'étiquette existe). */
export async function createTag(libraryId: string, label: string): Promise<Tag> {
  const res = await apiFetch(`/api/libraries/${libraryId}/tags`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ label }),
  });
  return tagSchema.parse(await readJson(res));
}

/**
 * `PATCH /api/libraries/:id/tags/:tagId` — met à jour un tag : renommage (`label`) et/ou
 * DESCRIPTION (`description` ; `null` ou chaîne vide → efface). Au moins un champ doit être
 * fourni. 409 `CONFLICT` si le renommage collisionne une étiquette existante.
 */
export async function updateTag(
  libraryId: string,
  tagId: string,
  patch: { label?: string; description?: string | null },
): Promise<Tag> {
  const res = await apiFetch(`/api/libraries/${libraryId}/tags/${tagId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return tagSchema.parse(await readJson(res));
}

/** Renomme un tag (raccourci sur `updateTag`). 409 `CONFLICT` si collision. */
export async function renameTag(libraryId: string, tagId: string, label: string): Promise<Tag> {
  return updateTag(libraryId, tagId, { label });
}

/** (Dé)fini la description d'un tag (`null`/vide efface) — raccourci sur `updateTag`. */
export async function setTagDescription(
  libraryId: string,
  tagId: string,
  description: string | null,
): Promise<Tag> {
  return updateTag(libraryId, tagId, { description });
}

/** `DELETE /api/libraries/:id/tags/:tagId` — supprime un tag + ses liaisons (204). */
export async function deleteTag(libraryId: string, tagId: string): Promise<void> {
  const res = await apiFetch(`/api/libraries/${libraryId}/tags/${tagId}`, { method: 'DELETE' });
  if (!res.ok) await readJson(res); // lève une ApiError typée
}

/**
 * `POST /api/libraries/:id/tags/merge` — fusionne des tags sources dans une cible.
 * Le corps est re-validé côté client avant l'envoi (on ne fait pas confiance non plus
 * à nos propres entrées) ; la réponse est re-parsée.
 */
export async function mergeTags(
  libraryId: string,
  body: MergeTagsRequest,
): Promise<MergeTagsResult> {
  const res = await apiFetch(`/api/libraries/${libraryId}/tags/merge`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return mergeTagsResultSchema.parse(await readJson(res));
}
